export const chatName = (chat) => {
    if (chat.type ===  'CONTACT') {
        return chat.kwik_contact_name || chat.name || chat.phone_num || '';
    } else {
        return chat.name || chat.phone_num || '';
    }
}

export const isGroupParticipantsUpdateMessage = (message) => {
    return message?.simplified_type === 'group_participants_update' || message?.messageType === 'groupParticipantsUpdate'
}

const normalizeGroupEventAction = (value = '') => {
    switch (String(value || '').trim().toLowerCase()) {
        case 'add':
        case 'added':
        case 'participant_added':
            return 'added'
        case 'left':
        case 'leave':
        case 'participant_left':
            return 'left'
        case 'remove':
        case 'removed':
        case 'participant_removed':
            return 'removed'
        case 'promote':
        case 'promoted':
            return 'promote'
        case 'demote':
        case 'demoted':
            return 'demote'
        default:
            return ''
    }
}

const normalizeArrayInput = (value) => {
    if (Array.isArray(value)) return value
    if (value === null || value === undefined) return []
    return [value]
}

const firstValue = (...values) => {
    return values.map((value) => String(value || '').trim())
        .find((value) => value && !value.toLowerCase().includes('[object object]')) || ''
}

const getParticipantKeys = (participant) => {
    if (typeof participant === 'string' || typeof participant === 'number') {
        participant = { id: participant }
    }

    if (!participant || typeof participant !== 'object') return []

    const id = firstValue(
        participant.participant,
        participant.participant_original,
        participant.participant_jid,
        participant.participant_lid,
        participant.jid,
        participant.id,
        participant.lid,
        participant.phoneNumber,
        participant.phone_number,
        participant.participant_phone
    )
    const digits = id.split('@')[0].replace(/:\d+$/, '').replace(/\D/g, '')

    return [id, digits].filter(Boolean).map((value) => String(value).trim().toLowerCase())
}

const getParticipantLabel = (participant, options = {}) => {
    if (typeof participant === 'string' || typeof participant === 'number') {
        participant = { id: participant }
    }

    if (!participant || typeof participant !== 'object') return ''

    const participantKeys = getParticipantKeys(participant)
    const connectedPhone = String(options.connectedPhone || '').replace(/\D/g, '')
    const isCurrentInstance = participant.participant_is_current_instance === true ||
        Boolean(connectedPhone && participantKeys.includes(connectedPhone))

    if (isCurrentInstance) return 'Você'

    const name = firstValue(
        participant.participant_display_name,
        participant.participantDisplayName,
        participant.display_name,
        participant.displayName,
        participant.contact_name,
        participant.contactName,
        participant.participant_name,
        participant.participantName,
        participant.name,
        participant.push_name,
        participant.pushName,
        participant.sender
    )
    if (name) return name

    if (options.participantDirectory instanceof Map) {
        const directoryLabel = participantKeys
            .map((key) => options.participantDirectory.get(key))
            .find(Boolean)
        if (directoryLabel) return directoryLabel
    }

    const id = participantKeys[0] || ''
    const digits = id.split('@')[0].replace(/:\d+$/, '').replace(/\D/g, '')
    if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) return `+${digits}`
    if (digits.length === 10 || digits.length === 11) return `+55${digits}`
    return digits || id
}

const joinParticipantNames = (names = []) => {
    const normalizedNames = names.filter(Boolean)
    if (!normalizedNames.length) return 'Participante'
    if (normalizedNames.length === 1) return normalizedNames[0]
    if (normalizedNames.length === 2) return `${normalizedNames[0]} e ${normalizedNames[1]}`
    return `${normalizedNames.slice(0, -1).join(', ')} e ${normalizedNames[normalizedNames.length - 1]}`
}

const extractParticipants = (payload) => {
    const dataEntries = [
        payload?.participantsData,
        payload?.participants_data,
        payload?.participantData,
        payload?.participant_data,
        payload?.members,
        payload?.member,
        payload?.users,
        payload?.user,
        payload?.recipient,
        payload?.recipients,
    ].flatMap(normalizeArrayInput)
        .filter((participant) => participant && typeof participant === 'object')

    if (dataEntries.length) return dataEntries

    return [
        payload?.participants,
        payload?.members,
        payload?.member,
        payload?.users,
        payload?.user,
        payload?.recipient,
        payload?.recipients,
        payload?.target,
        payload?.targets,
        payload?.participant,
    ].flatMap(normalizeArrayInput)
        .map((participant) => typeof participant === 'object' ? participant : { id: participant })
        .filter((participant) => getParticipantLabel(participant))
}

export const formatGroupParticipantsUpdateText = (message, options = {}) => {
    const payload = message?.message && typeof message.message === 'object' ? message.message : {}
    const mergedPayload = {
        ...(message && typeof message === 'object' ? message : {}),
        ...payload,
    }
    const action = normalizeGroupEventAction(
        mergedPayload?.group_event_action ||
        mergedPayload?.groupEventAction ||
        mergedPayload?.action
    )
    const participants = extractParticipants(mergedPayload)
    const author = {
        id: mergedPayload?.author,
        jid: mergedPayload?.author,
        participant_phone: mergedPayload?.authorPn || mergedPayload?.author_pn,
        participant_lid: mergedPayload?.authorLid || mergedPayload?.author_lid,
        participant_name: mergedPayload?.authorData?.name || mergedPayload?.author_data?.name || mergedPayload?.pushName || mergedPayload?.push_name || mergedPayload?.sender,
        participant_display_name: mergedPayload?.authorData?.name || mergedPayload?.author_data?.name || mergedPayload?.pushName || mergedPayload?.push_name || mergedPayload?.sender,
        pushName: mergedPayload?.authorData?.name || mergedPayload?.author_data?.name || mergedPayload?.pushName || mergedPayload?.push_name || mergedPayload?.sender,
    }
    const authorKeys = getParticipantKeys(author)
    const actorMatchesParticipants = authorKeys.length > 0 && participants.length > 0 &&
        participants.every((participant) => getParticipantKeys(participant).some((key) => authorKeys.includes(key)))
    const resolvedAction = action === 'removed' && actorMatchesParticipants ? 'left' : action
    const authorLabel = getParticipantLabel(author, options)
    let participantLabel = joinParticipantNames(participants.map((participant) => getParticipantLabel(participant, options)))
    if (actorMatchesParticipants && authorLabel) {
        participantLabel = authorLabel
    }

    switch (resolvedAction) {
        case 'added':
            return `${participantLabel} ${participants.length > 1 ? 'foram adicionados' : 'foi adicionado'} ao grupo`
        case 'left':
            return `${participantLabel} ${participants.length > 1 ? 'saíram' : 'saiu'} do grupo`
        case 'removed':
            return authorLabel && !actorMatchesParticipants ? `${authorLabel} removeu ${participantLabel}` : `${participantLabel} ${participants.length > 1 ? 'foram removidos' : 'foi removido'} do grupo`
        case 'promote':
            return `${participantLabel} ${participants.length > 1 ? 'viraram administradores' : 'virou administrador'}`
        case 'demote':
            return `${participantLabel} ${participants.length > 1 ? 'deixaram de ser administradores' : 'deixou de ser administrador'}`
        default:
            return 'Atualização de participantes do grupo'
    }
}
